import React, {Component, ErrorInfo, ReactNode} from 'react';
import {logError} from '@/shared/utils/logger';

interface Props {
    children: ReactNode;
    fallback?: ReactNode;
}

interface State {
    hasError: boolean;
    error?: Error;
}

/**
 * Error Boundary component that automatically tracks errors to Application Insights
 *
 * Usage:
 * <ErrorBoundary>
 *   <YourComponent />
 * </ErrorBoundary>
 */
class ErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {hasError: false};
    }

    static getDerivedStateFromError(error: Error): State {
        return {hasError: true, error};
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
        logError('Error caught by ErrorBoundary', error, {componentStack: errorInfo.componentStack});
    }

    render() {
        if (this.state.hasError) {
            return (
                this.props.fallback || (
                    <div className="flex items-center justify-center min-h-screen bg-gray-100">
                        <div className="bg-white p-8 rounded-lg shadow-md max-w-md">
                            <h1 className="text-2xl font-bold text-mld-danger mb-4">Something went wrong</h1>
                            <p className="text-gray-700 mb-4">
                                We&apos;re sorry for the inconvenience. The error has been logged and we&apos;ll look
                                into it.
                            </p>
                            <button
                                onClick={() => window.location.reload()}
                                className="bg-mld-info/50 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded">
                                Reload Page
                            </button>
                        </div>
                    </div>
                )
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
